import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Tooltip,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  VStack,
  Button,
  Text,
  Spinner,
  Box,
  Link,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  HStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiEdit, FiTrash2, FiDownload } from "react-icons/fi";
import { getExperimentsList, getExperimentById, updateExperiment, deleteExperiment, downloadExperimentOutput } from "../../services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { epochConverterWithTimeZone } from "../../utils/dateUtils";
import { useUser } from "context/UserContext";
import { Experiment, ExperimentList } from "build-ts/experiment";


const ExperimentTable: React.FC = () => {
  const hasMounted = useRef(false);
  const { defaultTimeZone } = useUser();

  // State for data
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Experiment filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    status: "all",
  });
  const filtersRef = useRef(filters);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("last_modified_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Edit modal state
  const { isOpen: isEditExperimentOpen, onOpen: onEditExperimentOpen, onClose: onEditExperimentClose } = useDisclosure();
  const [editingExperiment, setEditingExperiment] = useState<Experiment | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: ""
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Details modal state
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [detailsExperiment, setDetailsExperiment] = useState<Experiment | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Delete confirmation dialog state
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deletingExperiment, setDeletingExperiment] = useState<Experiment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Helper function to map table sort keys to backend enum values
  const mapSortByToBackend = (sortKey: string): string => {
    const mapping: Record<string, string> = {
      "created_date": "CREATED_DATE",
      "last_modified_date": "LAST_MODIFIED_DATE",
      "name": "NAME",
    };
    return mapping[sortKey] || sortKey.toUpperCase();
  };

  // Fetch experiments from Darwin API
  const fetchExperiments = useCallback(async (
      reset = false,
      currentSortBy = "last_modified_date",
      currentSortOrder = "desc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);

      // Map sort_by to backend enum format
      const backendSortBy = mapSortByToBackend(currentSortBy);
      const backendSortOrder = currentSortOrder.toUpperCase();

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: backendSortBy,
        sort_order: backendSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("search_string", currentFilters.search);
      }
      if (currentFilters.status && currentFilters.status !== "all") {
        params.append("status", currentFilters.status.toUpperCase());
      }

      const result: ExperimentList = await getExperimentsList(params);

      if (result.experiments) {
        // Always replace the experiments list, never append
        setExperiments(result.experiments);

        if (result.total) {
          const totalCount = result.total || 0;
          setPagination({
            total: totalCount,
            has_more: (currentOffset + limit) < totalCount,
            offset: currentOffset,
          });
        } else {
          setPagination({
            total: 0,
            has_more: false,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no experiments returned
        setExperiments([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchExperiments(true);
      };
      loadData();
    }
  }, [fetchExperiments]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchExperiments(false, sortBy, sortOrder, filters, offset);
  }, [fetchExperiments, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchExperiments(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchExperiments, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    // Also update the ref immediately
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchExperiments(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchExperiments, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", status: "all" };
    setFilters(clearedFilters);
    filtersRef.current = clearedFilters;
    setSortBy("last_modified_date");
    setSortOrder("desc");
    await fetchExperiments(true, "last_modified_date", "desc", clearedFilters, 0);
  }, [fetchExperiments]);

  const handleEditExperiment = (experiment: Experiment) => {
    setEditingExperiment(experiment);
    setEditFormData({
      name: experiment.header?.name || "",
      description: experiment.header?.description || "",
    });
    onEditExperimentOpen();
  };

  const handleExperimentRowClick = async (experiment: Experiment) => {
    setSelectedExperiment(experiment);
    setDetailsExperiment(null);
    setDetailsError(null);
    onDetailsOpen();

    const experimentId = experiment.header?.id;
    if (!experimentId) {
      setDetailsError("Experiment ID is missing.");
      return;
    }

    try {
      setIsDetailsLoading(true);
      const data = await getExperimentById(experimentId);
      if (data) {
        const parsed = data;
        setDetailsExperiment(parsed);
      } else {
        setDetailsError("Failed to load experiment details.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error while loading details.";
      setDetailsError(message);
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleUpdateExperiment = async () => {
    if (!editingExperiment) return;

    setIsUpdating(true);
    const response = await updateExperiment(editingExperiment.header?.id, {
      header: {
        name: editFormData.name,
        description: editFormData.description,
      }
    });

    // Only update the experiment in the list if the update was successful
    if (response) {
        setExperiments(prevExperiment =>
        prevExperiment.map(experiment =>
            experiment.header?.id === editingExperiment.header?.id
            ? {
                ...experiment,
                header: {
                  ...experiment.header,
                  name: response.header?.name,
                  description: response.header?.description,
                  last_modified_date: response.header?.last_modified_date,
                }
              }
            : experiment
        )
      );
      onEditExperimentClose();
      setEditingExperiment(null);
    }

    setIsUpdating(false);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteExperiment = (experiment: Experiment) => {
    setDeletingExperiment(experiment);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (!deletingExperiment) return;

    const experimentId = deletingExperiment.header?.id;
    if (!experimentId) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteExperiment(experimentId);
      // Remove the deleted experiment from the list
      setExperiments(prevExperiments =>
        prevExperiments.filter(exp => exp.header?.id !== experimentId)
      );
      // Update pagination total count
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
      onDeleteClose();
      setDeletingExperiment(null);
    } catch (error) {
      // Error is already handled in the deleteExperiment function via toast
      console.error("Error deleting experiment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadOutput = async () => {
    const experimentId = detailsExperiment?.header?.id || selectedExperiment?.header?.id;
    if (!experimentId) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadExperimentOutput(experimentId);
    } catch (error) {
      // Error is already handled in the downloadExperimentOutput function via toast
      console.error("Error downloading experiment output:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Table configuration
  const tableColumns: TableColumn<Experiment>[] = [
    {
      key: "name",
      label: "Experiment Name",
      sortable: true,
      className: "eva-name-column",
      render: (experiment) => (
        <Tooltip label={experiment.header?.name} placement="top">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} wordBreak="break-word" mb={0}>
            {experiment.header?.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (experiment) => (
        <Tooltip label={experiment.header?.description} placement="top">
          <Text fontSize="sm" color="gray.600" noOfLines={2} wordBreak="break-word" mb={0}>
            {experiment.header?.description}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "created_date",
      label: "Created",
      sortable: true,
      className: "eva-date-column",
      render: (experiment) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(experiment.header?.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date",
      label: "Modified",
      sortable: true,
      className: "eva-date-column",
      render: (experiment) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(experiment.header?.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "eva-status-column",
      render: (experiment) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {experiment.status}
        </Text>
      ),
    },
  ];

  const tableFilters: TableFilter[] = [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search experiments...",
      width: "300px",
    },
    {
      key: "status",
      type: "select",
      label: "Status",
      placeholder: "All statuses",
      width: "200px",
      options: [
        { value: "all", label: "All" },
        { value: "PENDING", label: "Pending" },
        { value: "PROCESSING", label: "Processing" },
        { value: "COMPLETED", label: "Completed" },
        { value: "FAILED", label: "Failed" },
      ],
    },
  ];

  const rowActions = (experiment: Experiment) => (
    <HStack spacing={1}>
      <Tooltip label="Edit Experiment">
        <IconButton
          icon={<FiEdit />}
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleEditExperiment(experiment);
          }}
          aria-label="Edit experiment"
        />
      </Tooltip>
      <Tooltip label="Delete Experiment">
        <IconButton
          icon={<FiTrash2 />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteExperiment(experiment);
          }}
          aria-label="Delete experiment"
        />
      </Tooltip>
    </HStack>
  );

  return (
    <>
      <Box height="100%" overflowY="hidden">
        <Table
          data={experiments}
          columns={tableColumns}
          loading={loading}
          error={error}
          pagination={pagination}
          limit={limit}
          onPageChange={handlePageChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          filters={tableFilters}
          filterValues={filters}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          rowActions={rowActions}
          onRowClick={handleExperimentRowClick}
          getItemId={(experiment) => experiment.header?.id || ""}
          emptyMessage="No experiments found"
          emptySubMessage="Try adjusting your search criteria"
        />
      </Box>

      {/* Experiment Edit Modal */}
      <Modal isOpen={isEditExperimentOpen} onClose={onEditExperimentClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent className="eva-modal-content">
          <ModalHeader>Edit Experiment</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar">
            {editingExperiment && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Experiment Name</FormLabel>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => handleEditFormChange("name", e.target.value)}
                    placeholder="Enter experiment name"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => handleEditFormChange("description", e.target.value)}
                    placeholder="Enter experiment description"
                    rows={6}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onEditExperimentClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateExperiment}
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              Update Experiment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Experiment Details Modal */}
      <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent className="eva-modal-content" bg="gray.50">
          <ModalHeader
            bg="white"
            borderBottom="1px solid"
            borderColor="gray.200"
            py={4}
            px={6}
          >
            <Text fontSize="lg" fontWeight="semibold" mb={0}>
              {detailsExperiment?.header?.name || selectedExperiment?.header?.name || "-"}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar" p={6}>
            {isDetailsLoading && (
              <Box display="flex" alignItems="center" justifyContent="center" minH="120px">
                <Spinner size="lg" />
              </Box>
            )}
            {!isDetailsLoading && detailsError && (
              <Box p={4} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
                <Text color="red.600" fontSize="sm" fontWeight="medium">
                  {detailsError}
                </Text>
              </Box>
            )}
            {!isDetailsLoading && !detailsError && (detailsExperiment || selectedExperiment) && (
              <VStack spacing={4} align="stretch">
                <HStack spacing={4} align="stretch">
                  <Box
                    flex={1}
                    p={4}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="semibold"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Input File Name
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {detailsExperiment?.input_file_name || "-"}
                    </Text>
                  </Box>
                  <Box
                    flex={1}
                    p={4}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="semibold"
                      mb={2}
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Status
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {detailsExperiment?.status}
                    </Text>
                  </Box>
                </HStack>
                <Box p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={3} textTransform="uppercase" letterSpacing="wide">
                    Description
                  </Text>
                  <Text fontSize="md" color="gray.800" fontWeight="medium" whiteSpace="pre-wrap">
                    {detailsExperiment?.header?.description || "-"}
                  </Text>
                </Box>
                <Box p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={3} textTransform="uppercase" letterSpacing="wide">
                    Chat Sessions
                  </Text>
                  <VStack spacing={2} align="start">
                    {detailsExperiment?.chat_headers && detailsExperiment?.chat_headers?.length > 0 ? (
                      detailsExperiment?.chat_headers.map((chatHeader) => (
                        <Link
                          key={chatHeader.id}
                          as={RouterLink}
                          to={`/eva/chat/${chatHeader.id}`}
                          fontSize="sm"
                          color="blue.500"
                          fontWeight="medium"
                          _hover={{ color: "blue.700", textDecoration: "underline" }}
                          py={1}
                          isExternal
                        >
                          {chatHeader.name}
                        </Link>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        No chat sessions available
                      </Text>
                    )}
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter
            bg="white"
            borderTop="1px solid"
            borderColor="gray.200"
            py={4}
            px={6}
          >
            <Button
              leftIcon={<FiDownload />}
              colorScheme="blue"
              onClick={handleDownloadOutput}
              isLoading={isDownloading}
              loadingText="Downloading..."
              mr={3}
              isDisabled={!detailsExperiment && !selectedExperiment}
            >
              Download Output
            </Button>
            <Button variant="outline" onClick={onDetailsClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Experiment Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Experiment
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete the experiment "{deletingExperiment?.header?.name}"?
              This will also delete associated chat sessions. This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onDeleteClose}
                variant="outline"
                isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ExperimentTable;
